import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-wrap align-content-center mb-3 gap-3 pl-6"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center p-3 pb-0"
}
const _hoisted_4 = { class: "flex flex-column w-full" }
const _hoisted_5 = { key: 0 }

import { watch, computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import DataTable from 'primevue/datatable';
import useVuelidate from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { PersonStores, useRelayHubStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS, MEDICAID_FEATUREABLE_TYPE } from '@/enum';
import { MedicaidEnabledOrganizationInterface } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'MedicaidBilling',
  setup(__props) {

const profileStore = PersonStores.useUserProfileStore();
const relayHubStore = useRelayHubStore();

const {
  medicaidEnabledOrganizations,
  selectedMedicaidOrganizationsId,
  originalMedicaidOrganizationsId,
  medicaidBilling,
  originalMedicaidEnabledOrganizations,
} = storeToRefs(profileStore);

const toggleMedicaidEnabled = ref<boolean>(medicaidBilling?.value?.medicaid_enabled || false);

const isToggledOn = (v: string, vm: MedicaidEnabledOrganizationInterface) =>
  selectedMedicaidOrganizationsId.value.find((id) => id === vm.id);

const rules = computed(() => {
  return {
    medicaidEnabledOrganizations: {
      $each: helpers.forEach({
        NPI: {
          requiredIfCustom: helpers.withMessage(
            'NPI is required if the organization is selected',
            requiredIf(isToggledOn),
          ),
        },
      }),
    },
  };
});

const v$ = useVuelidate(rules, { medicaidEnabledOrganizations });

const onSave = async () => {
  if (!v$.value.$error) {
    if (isMedicaidOrganizationsChanged()) {
      await profileStore.saveMedicaidBilling();
      profileStore.doToast('Medicaid Billing has been updated');
    }
    if (toggleMedicaidEnabled.value !== medicaidBilling?.value?.medicaid_enabled) {
      await saveMedicaidEnable();
    }
    await getBillingInfo();
    resetDirty();
  }
};

const resetDirty = () => {
  v$.value.$reset();
};

const onCancel = () => {
  resetDirty();
  getBillingInfo();
};

watch(
  () => profileStore.id,
  (newId) => {
    if (newId) {
      getBillingInfo();
    }
  },
);

const getBillingInfo = async () => {
  await profileStore.getMedicaidBilling();
  if (profileStore.error) {
    profileStore.doToast(profileStore.error as string, 'Error', true);
  }
};

const notChecked = (id: number) => {
  return !selectedMedicaidOrganizationsId.value.includes(id);
};

const resetRow = (item: MedicaidEnabledOrganizationInterface) => {
  if (notChecked(item.id)) {
    item.NPI = '';
  }
  v$.value.medicaidEnabledOrganizations.$touch();
};

const saveMedicaidEnable = async () => {
  const personId: number = profileStore.id as number;

  if (toggleMedicaidEnabled.value) {
    if (selectedMedicaidOrganizationsId.value.length > 0) {
      await relayHubStore.enableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);

      if (!relayHubStore.error) relayHubStore.doToast(`Service Provider is now Medicaid Enabled.`);
      else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
    } else {
      toggleMedicaidEnabled.value = false;
      relayHubStore.doToast(
        'At least one Medicaid Organization must be selected to enable Medicaid Billing.',
        'Error',
        true,
      );
    }
  } else {
    await relayHubStore.disableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);
    if (!relayHubStore.error) relayHubStore.doToast(`Service Provider is now Medicaid Disabled.`);
    else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
  }

  await getBillingInfo();
};

/**
 * Deep compares two arrays of MedicaidEnabledOrganizationInterface objects.
 * Returns true if any difference is found, false otherwise.
 */
const isMedicaidOrganizationsChanged = (): boolean => {
  const original = originalMedicaidEnabledOrganizations.value;
  const current = medicaidEnabledOrganizations.value;
  const originalIds = originalMedicaidOrganizationsId.value;
  const currentIds = selectedMedicaidOrganizationsId.value;

  // Compare arrays of IDs
  if (originalIds.length !== currentIds.length) return true;

  const idDiff = originalIds.find((id, i) => id !== currentIds[i]);
  if (idDiff !== undefined) return true;

  // Compare organization objects
  if (original.length !== current.length) return true;
  for (let i = 0; i < original.length; i++) {
    const orig = original[i];
    const curr = current[i];
    for (const key in orig) {
      if (Object.prototype.hasOwnProperty.call(orig, key)) {
        if (orig[key] !== curr[key]) return true;
      }
    }
  }
  return false;
};

return (_ctx: any,_cache: any) => {
  const _component_Checkbox = _resolveComponent("Checkbox")!
  const _component_Column = _resolveComponent("Column")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "Medicaid Billing",
    dirty: _unref(v$).$anyDirty,
    cancel: onCancel,
    save: onSave,
    "save-disabled": !_unref(can)([_unref(PERMISSIONS).PEOPLE_UPDATE]) || _unref(v$).$error
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        (_unref(medicaidEnabledOrganizations).length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_component_Checkbox, {
                id: "toggleMedicaidEnabled",
                modelValue: toggleMedicaidEnabled.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((toggleMedicaidEnabled).value = $event)),
                binary: true,
                onChange: _unref(v$).$touch
              }, null, 8, ["modelValue", "onChange"]),
              _cache[2] || (_cache[2] = _createElementVNode("label", {
                for: "toggleMedicaidEnabled",
                class: "mr-3"
              }, "Enable", -1))
            ]))
          : _createCommentVNode("", true),
        (!_unref(medicaidEnabledOrganizations).length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, " No Medicaid Billing Organizations found. "))
          : (_openBlock(), _createBlock(_unref(DataTable), {
              key: 2,
              scrollable: true,
              value: _unref(medicaidEnabledOrganizations),
              "responsive-layout": "scroll",
              class: "w-full",
              "scroll-height": "flex"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_Column, {
                  header: "Eligible",
                  class: "text-center justify-content-center px-3",
                  style: { maxWidth: '150px' }
                }, {
                  body: _withCtx((slotProps) => [
                    _createVNode(_component_Checkbox, {
                      modelValue: _unref(selectedMedicaidOrganizationsId),
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedMedicaidOrganizationsId) ? (selectedMedicaidOrganizationsId).value = $event : null)),
                      name: slotProps.data.name,
                      value: slotProps.data.id,
                      onChange: ($event: any) => (resetRow(slotProps.data))
                    }, null, 8, ["modelValue", "name", "value", "onChange"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "name",
                  header: "Organization Name"
                }),
                _createVNode(_component_Column, {
                  field: "NPI",
                  header: "Supervisor/NPI Code"
                }, {
                  body: _withCtx((slotProps) => [
                    _createElementVNode("div", _hoisted_4, [
                      _createVNode(_component_InputText, {
                        modelValue: slotProps.data.NPI,
                        "onUpdate:modelValue": ($event: any) => ((slotProps.data.NPI) = $event),
                        disabled: notChecked(slotProps.data.id),
                        onInput: _unref(v$).$touch
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onInput"]),
                      (_unref(v$).medicaidEnabledOrganizations.$dirty)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).medicaidEnabledOrganizations.$each.$response.$errors[slotProps.index].NPI, (error) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: error.$uid,
                                class: "input-errors"
                              }, [
                                _createElementVNode("small", null, _toDisplayString(error.$message), 1)
                              ]))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["value"]))
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})